<script lang="ts" setup>
import { tpgIconError } from '@tpg/grammar-web-icons';
import { useResizeObserver } from '@vueuse/core';
import {
  computed,
  onMounted,
  ref,
  toRefs,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';

import { GrammarIcon, GrammarQuaternaryButton } from '@nora-monorepo/grammar-ui';
import type { QuotedMessageResultDTO } from '@nora-monorepo/nora-api-client';
import { formatName } from '@nora-monorepo/shared';
import {
  bgBlue,
  bgOrange,
  bgPrimary,
  bgSecondary,
  borderBlue,
  HoverableUserName,
  textPrimary,
  textRed,
  textSecondary,
  textTertiary,
} from '@nora-monorepo/ui-shared';

import MessageAttachments from '#nora-chat-ui/components/open-conversations/messages/message-attachments/MessageAttachments.vue';
import MessageForwardContent from '#nora-chat-ui/components/open-conversations/messages/message-forward/MessageForwardContent.vue';
import MessageQuoteContent from '#nora-chat-ui/components/open-conversations/messages/message-quote/MessageQuoteContent.vue';
import MessageReactions from '#nora-chat-ui/components/open-conversations/messages/message-reactions/MessageReactions.vue';
import MessageContent from '#nora-chat-ui/components/open-conversations/messages/MessageContent.vue';
import MessageInteractionHandler from '#nora-chat-ui/components/open-conversations/messages/MessageInteractionHandler.vue';
import SeenBy from '#nora-chat-ui/components/open-conversations/messages/seen-by/SeenBy.vue';
import type { MessageState } from '#nora-chat-ui/states';
import { useConversationStore } from '#nora-chat-ui/stores/conversation/conversation-store';
import { useConversationMessageStore } from '#nora-chat-ui/stores/conversation/message/conversation-message-store';
import { useSeenByModalStore } from '#nora-chat-ui/stores/conversation/message/seen-by-modal-store';
import { useChatUserStore } from '#nora-chat-ui/stores/user-store';

const props = defineProps<ViewMessageProps>();

const { d, t } = useI18n();

interface ViewMessageProps {
  messageState: MessageState;
  containerRef: HTMLElement | null;
  isNewest: boolean;
  showDate: boolean;
  isMyLastSentMessage: boolean;
  isMyLastReceivedMessage: boolean;
  showSeenBy: boolean;
  operationId?: string | null;
}

const {
  messageState,
  showDate,
  isMyLastSentMessage,
  isMyLastReceivedMessage,
} = toRefs(props);

const userStore = useChatUserStore();
const conversationStore = useConversationStore();
const conversationMessageStore = useConversationMessageStore();
const seenByModalStore = useSeenByModalStore();
const showFullDate = ref(false);
const isInView = ref(false);
const isLongMessage = ref(false);
const isMessageExpanded = ref(false);
const messageRowRef = ref<HTMLElement | null>(null);
const messageContainerRef = ref<HTMLElement | null>(null);
const messageContentRef = ref<HTMLElement | null>(null);

const hasAttachments = computed(() =>
  !messageState.value.message.deletedAt && Boolean(messageState.value.message.attachments?.length),
);
const hasAttachmentFooter = computed(() =>
  Boolean(messageState.value.message.quotedFrom || messageState.value.message.message),
);

let highlightTimer: ReturnType<typeof setTimeout> | null = null;
const HIGHLIGHT_TIMEOUT_MS = 5000;
const COLLAPSED_MESSAGE_HEIGHT_PX = 240;

const isMyMessage = computed(
  () => messageState.value.message.sentBy.id === userStore.user?.id,
);

const showMessageStatus = computed(
  () => isMyMessage.value && !messageState.value.message.deletedAt
    && (props.isNewest || messageState.value.status === 'error' || messageState.value.status === 'sending'),
);

const isBeingEdited = computed(() => {
  const messageEditId
    = conversationMessageStore.editingMessage?.oldMessage.message.id;
  return messageEditId === messageState.value.message.id;
});

useResizeObserver(messageContentRef, ([entry]) => {
  isLongMessage.value
    = entry.contentRect.height > COLLAPSED_MESSAGE_HEIGHT_PX;

  if (!isLongMessage.value) {
    isMessageExpanded.value = false;
  }
});

watch(
  () => [
    messageState.value.message.id,
    messageState.value.message.message,
  ],
  () => {
    isMessageExpanded.value = false;
  },
);

function toggleMessageExpanded() {
  isMessageExpanded.value = !isMessageExpanded.value;
}

const isHighlighted = computed(() => {
  const conversationState = conversationStore.getConversation(
    messageState.value.message.conversationId,
  );
  return conversationState?.highlightedMessage?.id === messageState.value.message.id;
});

const bubbleBackground = computed(() => {
  if (isHighlighted.value && !isBeingEdited.value) {
    return bgOrange;
  }

  if (messageState.value.message.deletedAt) {
    return bgPrimary;
  }

  return isMyMessage.value ? bgBlue : bgSecondary;
});

const attachmentBadgeClasses = computed(() =>
  hasAttachments.value ? ['rounded-md px-2 py-1', bubbleBackground.value] : [],
);

function clearHighlightAfterTimer() {
  if (highlightTimer) {
    clearTimeout(highlightTimer);
  }
  highlightTimer = setTimeout(() => {
    const conversationState = conversationStore.getConversation(
      messageState.value.message.conversationId,
    );
    const isCurrentMessageHighlighted = conversationState?.highlightedMessage?.id === messageState.value.message.id;

    if (isCurrentMessageHighlighted && conversationState) {
      conversationState.highlightedMessage = undefined;
    }
  }, HIGHLIGHT_TIMEOUT_MS);
}

watch(isHighlighted, (newValue) => {
  if (newValue) {
    clearHighlightAfterTimer();
  }
});

const MIN_REACTIONS = 2;
const MAX_REACTIONS_NO_MESSAGE = 4;
const MAX_REACTIONS = 15;
const APPROXIMATE_REACTION_WIDTH = 22;

const maxReactions = computed(() => {
  if (!messageContainerRef.value) {
    return MAX_REACTIONS_NO_MESSAGE;
  }

  const width = messageContainerRef.value.clientWidth;
  const widthReactions = Math.floor(width / APPROXIMATE_REACTION_WIDTH);
  const maxReactions = Math.max(MIN_REACTIONS, widthReactions);

  return Math.min(maxReactions, MAX_REACTIONS);
});

function handleIntersection(entries: IntersectionObserverEntry[]) {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      conversationMessageStore.setMessageIsInView(messageState.value, true);

      isInView.value = true;
    }
    else {
      conversationMessageStore.setMessageIsInView(messageState.value, false);

      isInView.value = false;
    }
  });
}

let intersectionObserverInitialized = false;

watch(() => props.containerRef, setupIntersectionObserver, {
  immediate: true,
});

function openSeenByModal() {
  seenByModalStore.openModal(
    messageState.value.message.id,
    messageState.value.message.conversationId,
  );
}

function toggleShowFullDate() {
  showFullDate.value = !showFullDate.value;
}

function handleQuotedMessageClick(message?: QuotedMessageResultDTO | null) {
  if (!message) {
    return;
  }

  const conversationState = conversationStore.getConversation(
    messageState.value.message.conversationId,
  );

  if (conversationState) {
    conversationState.highlightedMessage = message;
  }
}

watch(messageRowRef, setupIntersectionObserver, {
  immediate: true,
});

function setupIntersectionObserver() {
  if (intersectionObserverInitialized) {
    return;
  }
  if (isMyMessage.value) {
    return;
  }
  if (!messageRowRef.value) {
    return;
  }
  if (!props.containerRef) {
    return;
  }

  const options = {
    root: props.containerRef,
    rootMargin: '0px',
    threshold: 0,
  };

  const observer = new IntersectionObserver(handleIntersection, options);
  observer.observe(messageRowRef.value);

  intersectionObserverInitialized = true;
}

onMounted(() => {
  if (isHighlighted.value) {
    clearHighlightAfterTimer();
  }
});
</script>

<template>
  <div v-if="showFullDate">
    <p class="text-center p-2 text-sm" :class="[textSecondary]">
      {{ d(messageState.message.createdAt, 'long') }}
    </p>
  </div>
  <div v-else-if="showDate">
    <p class="text-center p-2 text-sm" :class="[textSecondary]">
      {{ d(messageState.message.createdAt, 'date') }}
    </p>
  </div>

  <div
    ref="messageRowRef"
    class="py-1.5 px-3 flex w-full"
    :class="[{ 'flex-row-reverse': isMyMessage }]"
    data-name="conversation-message"
  >
    <div
      class="flex flex-row gap-1 items-center w-full"
      :class="[
        {
          'flex-row-reverse': isMyMessage,
        },
      ]"
    >
      <div class="flex flex-col flex-wrap max-w-[calc(100%-72px)] min-w-0">
        <MessageInteractionHandler
          :max-reactions="maxReactions"
          :message-state="messageState"
        >
          <div class="max-w-full flex flex-col items-end">
            <div
              ref="messageContainerRef"
              data-name="message-bubble"
              class="rounded-2xl max-w-full relative"
              :class="[
                bubbleBackground,
                hasAttachments ? 'w-min' : 'py-2 px-3 w-fit',
                {
                  'rounded-br-none':
                    isMyLastSentMessage && !messageState.message.deletedAt,
                  'rounded-bl-none':
                    isMyLastReceivedMessage && !messageState.message.deletedAt,
                  [`${textSecondary} ${borderBlue}`]:
                    messageState.message.deletedAt,
                  'text-label-on-accent-primary':
                    isMyMessage && !messageState.message.deletedAt,
                  [textPrimary]:
                    !isMyMessage && !messageState.message.deletedAt,
                  'border-2 border-[var(--colorFormBorderHighlightHighEmphasis)]':
                    isBeingEdited,
                },
              ]"
              @click="toggleShowFullDate"
            >
              <div
                class="flex text-xs font-semibold leading-none"
                :class="[
                  hasAttachments
                    ? 'message-header-overlay absolute top-2 left-2 right-2 z-10 items-start gap-1'
                    : [
                      'mb-1 w-full relative',
                      isMyMessage && !messageState.message.deletedAt
                        ? 'text-label-on-accent-secondary'
                        : textSecondary,
                    ],
                  isMyMessage ? 'justify-end' : 'justify-between',
                ]"
              >
                <MessageForwardContent
                  v-if="
                    !messageState.message.deletedAt
                      && messageState.message.forwardedFromId
                  "
                  :forwarded-by="
                    !isMyMessage
                      ? formatName(messageState.message.sentBy)
                      : null
                  "
                  :class="attachmentBadgeClasses"
                />
                <HoverableUserName
                  v-else-if="!isMyMessage"
                  class="message-sender-name overflow-hidden text-ellipsis shrink max-w-32 whitespace-nowrap mr-2"
                  :class="attachmentBadgeClasses"
                  :user="messageState.message.sentBy"
                />

                <span
                  class="flex shrink-0"
                  :class="attachmentBadgeClasses"
                >
                  <span v-if="messageState.message.isEdited" class="italic">
                    {{ t('message.editedMessage') }}&nbsp;
                  </span>
                  <span>{{ d(messageState.message.createdAt, 'time') }}</span>
                </span>
              </div>

              <MessageAttachments
                v-if="messageState.message.attachments?.length"
                data-name="message-attachments"
                class="overflow-hidden"
                :class="{
                  'message-media': hasAttachments,
                  'message-media-with-footer': hasAttachments && hasAttachmentFooter,
                }"
                :attachments="messageState.message.attachments"
                :is-my-message="isMyMessage"
                :conversation-id="messageState.message.conversationId"
                :message-id="messageState.message.id"
              />

              <div
                v-if="!hasAttachments || hasAttachmentFooter"
                :class="{ 'py-2 px-3': hasAttachments }"
              >
                <MessageQuoteContent
                  v-if="
                    !messageState.message.deletedAt
                      && messageState.message.quotedFrom
                  "
                  :message="messageState.message.quotedFrom"
                  :mine="isMyMessage"
                  @click="
                    handleQuotedMessageClick(messageState.message.quotedFrom)
                  "
                />

                <div
                  class="overflow-hidden"
                  :inert="isLongMessage && !isMessageExpanded"
                  :class="{
                    'message-content-clipped':
                      isLongMessage && !isMessageExpanded,
                  }"
                  :style="{
                    maxHeight:
                      isLongMessage && !isMessageExpanded
                        ? `${COLLAPSED_MESSAGE_HEIGHT_PX}px`
                        : undefined,
                  }"
                >
                  <div ref="messageContentRef">
                    <MessageContent
                      :message-state="messageState"
                      :is-mine="isMyMessage"
                      :operation-id="props.operationId"
                    />
                  </div>
                </div>
              </div>

              <div
                v-if="isLongMessage"
                class="flex justify-center mt-1"
                @click.stop
              >
                <GrammarQuaternaryButton
                  size="small"
                  :class="{
                    'message-expand-button-on-accent':
                      isMyMessage && !messageState.message.deletedAt,
                  }"
                  :label="
                    isMessageExpanded
                      ? t('message.showLess')
                      : t('message.showEntireMessage')
                  "
                  @click="toggleMessageExpanded"
                />
              </div>
            </div>

            <div
              class="flex w-fit"
              style="z-index: 1"
              :class="{
                'flex-row-reverse': !isMyMessage,
              }"
            >
              <MessageReactions
                :message-state="messageState"
                :max-reactions="maxReactions"
              />

              <transition name="message-status" mode="out-in">
                <p
                  v-if="showMessageStatus"
                  :key="messageState.status"
                  role="status"
                  class="text-xs ml-[0.4rem] flex items-center gap-1"
                  :class="[
                    messageState.status === 'error' ? textRed : textSecondary,
                  ]"
                >
                  <template v-if="messageState.status === 'sending'">
                    {{ t('conversation.messageSending') }}
                  </template>
                  <template v-else-if="messageState.status === 'error'">
                    <GrammarIcon
                      :icon-template="tpgIconError"
                      size="small"
                      style="--iconVectorFill: currentColor"
                      aria-hidden="true"
                    />
                    {{ t('conversation.messageError') }}
                    <GrammarQuaternaryButton
                      size="small"
                      :label="t('common.retry')"
                      @click.stop="conversationMessageStore.retryMessage(messageState)"
                    />
                  </template>
                  <template v-else-if="messageState.status === 'sent'">
                    {{ t('conversation.messageSent') }}
                  </template>
                </p>
              </transition>
            </div>
          </div>
        </MessageInteractionHandler>
      </div>

      <div
        class="self-end flex"
        :class="[
          textTertiary,
          !messageState.message.deletedAt ? 'flex' : 'hidden',
          {
            'mb-4':
              messageState.status || messageState?.message?.reactions?.length,
          },
        ]"
      >
        <SeenBy
          :show-seen-by="showSeenBy"
          @show-seen-by-modal="openSeenByModal"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.message-header-overlay :deep(.message-sender-name > p) {
  color: inherit;
  font-size: inherit;
  line-height: inherit;
}

.message-media {
  border-radius: inherit;
}

.message-media-with-footer {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.message-status-enter-active,
.message-status-leave-active {
  transition: opacity 100ms ease-out;
}

.message-status-enter-from,
.message-status-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .message-status-enter-active,
  .message-status-leave-active {
    transition: none;
  }
}

.message-content-clipped {
  mask-image: linear-gradient(to bottom, black calc(100% - 3rem), transparent);
  -webkit-mask-image:
    linear-gradient(to bottom, black calc(100% - 3rem), transparent);
}

.message-expand-button-on-accent {
  --buttonQuaternaryColorForegroundRegularEnabled:
    var(--label-on-accent-primary);
  --buttonQuaternaryColorForegroundRegularHover:
    var(--label-on-accent-primary);
  --buttonQuaternaryColorForegroundRegularPressed:
    var(--label-on-accent-primary);
}
</style>
